import React from "react";
import dayjs from "dayjs";
import { t, Trans } from "@lingui/macro";
import { XIcon } from "@heroicons/react/outline";
import { Alert } from "../../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../../../UI/Button/Button";
import { Loader } from "../../../../../UI/Loader/Loader";
import { InputText } from "../../../../../UI/InputText/InputText";
import { getTransStatus } from "../../../../../../constants/ticketsStatus";
import { selectDashboardLoader } from "../../../../../../model/system/selectors";
import { typeTicketResponse } from "../../../../../../model/dashboardTickets/dTicketsReducer";
import {
  dTicketsAddResponseItemSagaAC,
  dTicketsSetResponseViewedItemSagaAC,
} from "model/dashboardTickets/dTicketsActionCreators";
import {
  selectTicketsFilters,
  selectTicketsDetailItem,
} from "../../../../../../model/dashboardTickets/selectors";

export type typePopupDetails = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PopupDetails: React.FC<typePopupDetails> = ({
  variant,
  setOpenPopup,
}) => {
  const dispatch = useDispatch();

  const filters = useSelector(selectTicketsFilters);
  const loader = useSelector(selectDashboardLoader);
  const dataObject = useSelector(selectTicketsDetailItem);

  const transStatus = getTransStatus(dataObject!.status!);

  React.useEffect(() => {
    if (dataObject!.responses!.length) {
      dataObject!.responses!.forEach((item) => {
        if (item.viewedByUser === false) {
          const dataEditObject = {
            id: item.id!,
            userCreated: item.userCreated,
            dateCreated: item.dateCreated,
            text: item.text,
            viewedBySupport: item.viewedBySupport,
            viewedByUser: true,
          };
          dispatch(
            dTicketsSetResponseViewedItemSagaAC(dataEditObject, filters),
          );
        }
      });
    }

    // eslint-disable-next-line
  }, []);

  const responseList = dataObject!.responses!;
  const comments = responseList.length
    ? [...responseList].sort((a, b) => {
        const c = new Date(a!.dateCreated!).getTime();
        const d = new Date(b!.dateCreated!).getTime();
        return c - d;
      })
    : [];

  const [commentValue, setCommentValue] = React.useState("");

  const onClearHandler = () => {
    setCommentValue("");
  };

  const onSaveHandler = async (e: React.FormEvent) => {
    if (commentValue.trim() === "") return;
    e.preventDefault();

    const dataEditObject: typeTicketResponse = {
      id: dataObject!.id,
      text: commentValue.trim(),
    };

    dispatch(dTicketsAddResponseItemSagaAC(dataEditObject, filters));

    onClearHandler();
  };

  let dateCreated = "-";
  if (dataObject?.dateCreated) {
    dateCreated = dayjs(dataObject.dateCreated).format("DD-MM-YYYY, HH:mm");
  }

  return dataObject ? (
    <>
      <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 border-t-0 border-b">
          <div className="text-gray-500 text-left">
            <Trans>Creation date</Trans>:
          </div>
          <div className="text-gray-900 text-left ">{dateCreated}</div>
        </div>
        <div className="flex flex-wrap justify-end gap-x-4 py-2 border-t-0   border-b ">
          <div className="text-gray-500 text-left">
            <Trans>Status</Trans>:
          </div>
          <div className="text-gray-900 text-right ">
            {dataObject.status ? transStatus : "-"}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 py-2  border-b sm:col-span-2 ">
          <div className="text-gray-500 text-left">
            <Trans>Title</Trans>:
          </div>
          <div className="text-gray-900 text-left break-all">
            {dataObject?.name ? dataObject.name : " - "}
          </div>
        </div>
        <div className="flex flex-wrap  gap-x-4 py-2 border-b sm:col-span-2">
          <div className="text-gray-500 text-left">
            <Trans>Description</Trans>:
          </div>
          <div className="text-gray-900 text-left break-all">
            {dataObject.description ? dataObject.description : " - "}
          </div>
        </div>
        <div className="flex flex-col gap-x-4 py-2 border-b sm:col-span-2">
          <div className="text-gray-500 text-left">
            <Trans>Comments</Trans>:
          </div>
          <div className="text-gray-900 text-left divide-y">
            {comments.length
              ? comments.map((item, index) => {
                  let createdAt = "-";
                  if (item.dateCreated) {
                    createdAt = dayjs(item.dateCreated).format(
                      "M/D/YYYY, h:mm:ss A",
                    );
                  }

                  return (
                    <div className="grid grid-cols-2 py-3" key={index + 1}>
                      <div className="flex justify-between sm:flex-col">
                        <span className="font-light text-xs leading-4 mb-2">
                          {createdAt}
                        </span>
                      </div>
                      <div className="text-right ">
                        {item.viewedByUser === false && (
                          <span className="text-sm leading-5 font-medium text-yellow-500">
                            <Trans>New message</Trans>
                          </span>
                        )}
                      </div>
                      <p className="text-base leading-none font-normal col-span-2 break-all">
                        {item.text}
                      </p>
                    </div>
                  );
                })
              : " - "}
          </div>
        </div>
        <form className="flex  gap-x-4 py-2  sm:col-span-2">
          <div className="relative rounded-md w-full ">
            <InputText
              id="comment"
              name="comment"
              maxLength={150}
              value={commentValue}
              inputClassName="pr-6"
              placeholder={t`Enter your comment`}
              onChange={(e) => setCommentValue(e.target.value)}
            />
            {commentValue ? (
              <div
                className="absolute inset-y-0 right-0 pl-1.5 pr-1.5 flex items-center pointer-events-auto cursor-pointer"
                onClick={() => onClearHandler()}
              >
                <XIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
            ) : null}
          </div>
          <Button
            type="submit"
            disabled={
              commentValue === "" || commentValue.length > 150 || loader
            }
            label={<Trans>Send</Trans>}
            onClick={(e) => onSaveHandler(e)}
            buttonClassName={`${commentValue.trim() === "" ? "hidden" : "block"}`}
          />

          {loader ? <Loader /> : null}
        </form>
      </div>
      <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:col-span-2 justify-end ">
        <Button
          type="button"
          secondary={true}
          variant={variant}
          label={<Trans>Close</Trans>}
          onClick={() => setOpenPopup(false)}
        />
      </div>
    </>
  ) : (
    <Alert type="error" title={<Trans>Have no data.</Trans>} />
  );
};
